import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button, Col, Row, Spinner } from "react-bootstrap";
import PlayerEpicCard from "./PlayerEpicCard";
import SkeletonBlock from "../common/SkeletonBlock";
import "./epicLeaderboard.css";
import "../common/skeleton.css";
import type { RangeOption } from "../../constants/epicLeaderboard";
import type { PlayerEpicStatsResponse } from "../../types/epicLeaderboard";
import { analytics } from "../../utils/analytics";
import {
  fetchPlayerEpicStats,
  peekPlayerEpicStats
} from "../../utils/playerEpicApi";
import { rangeToStartDate } from "../../utils/rangeToStartDate";

// Same look as the section headings on the rest of the Player page.
const headingStyle = {
  color: "rgb(70, 130, 180)",
  marginBottom: "16px",
  marginTop: "32px"
};

// Placeholder for the first load. The section may turn out not to exist (no
// small-group runs), so it only hints at a heading and a couple of cards.
const EpicSectionSkeleton = () => (
  <section className="mb-5" role="status" aria-label="Loading epic stats">
    <SkeletonBlock
      width={90}
      height={26}
      style={{ marginTop: 32, marginBottom: 16 }}
    />
    <Row className="g-3">
      {[0, 1].map((i) => (
        <Col key={i} xs={12} md={6} xl={4}>
          <SkeletonBlock height={330} style={{ borderRadius: 12 }} />
        </Col>
      ))}
    </Row>
  </section>
);

type Props = {
  playerName: string;
  range: RangeOption;
  // The section hides itself for a player with no runs at all; the Epics tab
  // sets this to say so instead of showing nothing.
  showWhenNoRuns?: boolean;
};

// Everything the epic cache can already answer for this player + range, so
// coming back to the tab doesn't flash a skeleton.
const seedFromCache = (playerName: string, range: RangeOption) => {
  const startDate = rangeToStartDate(range);
  const hit = peekPlayerEpicStats(playerName, startDate);
  if (!hit) return null;
  if (hit.epics.length > 0) return { data: hit, any: true };
  if (startDate === undefined) return { data: hit, any: false };
  const allTime = peekPlayerEpicStats(playerName, undefined);
  return allTime ? { data: hit, any: allTime.epics.length > 0 } : null;
};

const PlayerEpicStats = ({
  playerName,
  range,
  showWhenNoRuns = false
}: Props) => {
  const [seed] = useState(() => seedFromCache(playerName, range));
  const [data, setData] = useState<PlayerEpicStatsResponse | null>(
    seed?.data ?? null
  );
  const [loading, setLoading] = useState(seed === null);
  const [error, setError] = useState(false);
  // Whether the player has any small-group run at all time, regardless of the
  // selected range. null until the first load answers it; false hides the
  // whole section.
  const [hasAnyRuns, setHasAnyRuns] = useState<boolean | null>(
    seed?.any ?? null
  );
  const [reload, setReload] = useState(0);
  const loadedPlayerRef = useRef(playerName);

  useEffect(() => {
    if (!playerName) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    if (loadedPlayerRef.current !== playerName) {
      loadedPlayerRef.current = playerName;
      setData(null);
      setHasAnyRuns(null);
    }

    const load = async () => {
      try {
        const startDate = rangeToStartDate(range);
        const result = await fetchPlayerEpicStats(
          playerName,
          startDate,
          controller.signal
        );
        let any = result.epics.length > 0;
        // Empty for this range only says something about the range; check all
        // time to tell "quiet lately" from "never ran one".
        if (!any && startDate !== undefined) {
          any =
            (await fetchPlayerEpicStats(playerName, undefined, controller.signal))
              .epics.length > 0;
        }
        if (controller.signal.aborted) return;
        setData(result);
        setHasAnyRuns(any);
        setLoading(false);
      } catch (err) {
        if (axios.isCancel(err) || controller.signal.aborted) return;
        console.error("Failed to fetch player epic stats", err);
        analytics.trackError("player_epic_stats", String((err as Error)?.message));
        setError(true);
        setLoading(false);
      }
    };
    load();

    return () => controller.abort();
  }, [playerName, range, reload]);

  if (!playerName) return null;
  if (hasAnyRuns === false) {
    return showWhenNoRuns ? (
      <div className="epic-lb-card text-center mt-4 mb-5">
        No small-group epic runs recorded for this player.
      </div>
    ) : null;
  }

  // First load for this player: nothing to title yet, since the section may
  // turn out not to exist.
  if (hasAnyRuns === null && loading) return <EpicSectionSkeleton />;

  // Later loads (a new range) keep the previous cards, dimmed.
  const refetching = loading && hasAnyRuns === true;

  return (
    <section className="mb-5">
      <div className="d-flex align-items-center gap-3">
        <h3 style={headingStyle}>EPICS</h3>
        {refetching && (
          <Spinner
            animation="border"
            variant="light"
            size="sm"
            className="mt-4 mb-3"
            role="status"
          >
            <span className="visually-hidden">Loading</span>
          </Spinner>
        )}
      </div>

      {error ? (
        <div className="epic-lb-card text-center">
          <div className="mb-2">Couldn't load epic stats.</div>
          <Button
            size="sm"
            variant="outline-light"
            onClick={() => setReload((n) => n + 1)}
          >
            Retry
          </Button>
        </div>
      ) : (
        <div className={refetching ? "is-refetching" : undefined}>
          {data && data.epics.length === 0 ? (
            <div className="epic-lb-card text-center">
              No small-group epic runs in this range.
            </div>
          ) : (
            <Row className="g-3">
              {data?.epics.map((stat) => (
                <Col key={stat.epic} xs={12} md={6} xl={4}>
                  <PlayerEpicCard
                    stat={stat}
                    player={data.player}
                    range={range}
                  />
                </Col>
              ))}
            </Row>
          )}
        </div>
      )}
    </section>
  );
};

export default PlayerEpicStats;
